import { Link, useSearch } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { ArrowLeft, MailCheck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { zodField } from "@/components/form/validators";
import { Button } from "@/components/ui/button";
import { register } from "@/lib/auth/auth-api";
import type { ApiError } from "@/lib/api/client";

import { AuthScreen } from "./auth-screen";
import { AuthField, PasswordInput, TextInput } from "./auth-fields";

function BackToLogin() {
  const { t } = useTranslation();
  return (
    <Link
      to="/login"
      className="mb-1.5 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-4" aria-hidden />
      {t("auth.signup.backToLogin")}
    </Link>
  );
}

/**
 * Account creation (parity with the legacy `account_signup`). Posts to the
 * register endpoint, which creates the user and sends an allauth verification
 * email (verification is mandatory) — so on success we show a "check your
 * email" screen rather than logging in; the user verifies via the emailed link,
 * then signs in.
 */
export function SignupPage() {
  const { t } = useTranslation();
  // token + email arrive when the user came from an invitation link (A-5).
  const search = useSearch({ from: "/signup" });
  const [formError, setFormError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const form = useForm({
    defaultValues: {
      // Name is collected later at the questionnaire identification step, like
      // the Django signup (A-7) — signup is email + password only.
      email: search.email ?? "",
      password1: "",
      password2: "",
    },
    onSubmit: async ({ value }) => {
      setFormError(null);
      if (value.password1 !== value.password2) {
        setFormError(t("auth.signup.passwordMismatch"));
        return;
      }
      try {
        // Create the account; verification is mandatory (the API blocks login
        // until the emailed link is clicked — see the token endpoint), so we
        // do NOT auto-log-in. Show a "check your email" screen; the user
        // confirms via the emailed link, then signs in. An invitation token
        // is recorded server-side at register and applied when that link
        // confirms the invited address (an address-less link applies at once).
        await register({ ...value, invitation_token: search.token });
        setSentTo(value.email.trim());
      } catch (err) {
        const apiErr = err as ApiError;
        // Surface the most useful backend error (duplicate email / weak
        // password) when present; otherwise a generic message.
        const fieldMsg = apiErr?.fieldErrors
          ? Object.values(apiErr.fieldErrors).flat()[0]
          : undefined;
        setFormError(fieldMsg ?? apiErr?.detail ?? t("auth.genericError"));
      }
    },
  });

  if (sentTo) {
    return (
      <AuthScreen>
        <div className="space-y-4">
          <MailCheck className="size-8 text-primary" aria-hidden />
          <h1 className="text-2xl font-extrabold">
            {t("auth.signup.sentTitle")}
          </h1>
          <p className="text-[14.5px] text-muted-foreground">
            {t("auth.signup.sentBody", { email: sentTo })}
          </p>
          <Button asChild className="mt-2">
            <Link to="/login">{t("auth.signup.backToLogin")}</Link>
          </Button>
        </div>
      </AuthScreen>
    );
  }

  const required = z.string().min(1, t("auth.signup.required"));
  const emailSchema = z.string().email(t("auth.emailRequired"));

  return (
    <AuthScreen>
      <BackToLogin />
      <h1 className="text-2xl font-extrabold">{t("auth.signup.title")}</h1>
      <p className="mt-1.5 text-[14.5px] text-muted-foreground">
        {t("auth.signup.subtitle")}
      </p>

      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.Field name="email" validators={{ onBlur: zodField(emailSchema) }}>
          {(field) => (
            <AuthField
              label={t("auth.email")}
              htmlFor="signup-email"
              error={field.state.meta.errors[0]?.toString()}
            >
              <TextInput
                id="signup-email"
                type="email"
                autoComplete="email"
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            </AuthField>
          )}
        </form.Field>

        <form.Field name="password1" validators={{ onBlur: zodField(required) }}>
          {(field) => (
            <AuthField
              label={t("auth.signup.password")}
              htmlFor="signup-password1"
              error={field.state.meta.errors[0]?.toString()}
            >
              <PasswordInput
                id="signup-password1"
                autoComplete="new-password"
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            </AuthField>
          )}
        </form.Field>

        <form.Field name="password2" validators={{ onBlur: zodField(required) }}>
          {(field) => (
            <AuthField
              label={t("auth.signup.passwordConfirm")}
              htmlFor="signup-password2"
              error={field.state.meta.errors[0]?.toString()}
            >
              <PasswordInput
                id="signup-password2"
                autoComplete="new-password"
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            </AuthField>
          )}
        </form.Field>

        {formError && (
          <p role="alert" className="mt-4 text-sm font-medium text-destructive">
            {formError}
          </p>
        )}

        <form.Subscribe selector={(s) => s.isSubmitting}>
          {(isSubmitting) => (
            <Button type="submit" className="mt-5 w-full" disabled={isSubmitting}>
              {isSubmitting ? t("auth.submitting") : t("auth.signup.submit")}
            </Button>
          )}
        </form.Subscribe>
      </form>
    </AuthScreen>
  );
}
